import { useEffect, useState } from 'react';

export const useClipboardAvailable = () => {
  const [available, setAvailable] = useState(() => {
    const clipboardAvailable =
      typeof navigator !== 'undefined' && !!navigator.clipboard;

    return {
      write: clipboardAvailable,
      read: clipboardAvailable,
    };
  });

  useEffect(() => {
    if (
      typeof navigator === 'undefined' ||
      !navigator.clipboard ||
      !navigator.permissions
    )
      return;

    let mounted = true;
    let writePermissionStatus: PermissionStatus | null = null;
    let readPermissionStatus: PermissionStatus | null = null;

    const handleWriteChange = () => {
      if (mounted && writePermissionStatus) {
        setAvailable((prev) => ({
          ...prev,
          write: writePermissionStatus!.state !== 'denied',
        }));
      }
    };

    const handleReadChange = () => {
      if (mounted && readPermissionStatus) {
        setAvailable((prev) => ({
          ...prev,
          read: readPermissionStatus!.state !== 'denied',
        }));
      }
    };

    navigator.permissions
      .query({ name: 'clipboard-write' as PermissionName })
      .then((result) => {
        writePermissionStatus = result;

        if (mounted) {
          setAvailable((prev) => ({
            ...prev,
            write: result.state !== 'denied',
          }));
          result.addEventListener('change', handleWriteChange);
        }
      })
      .catch(() => {
        // clipboard-write not queryable (e.g. Firefox), keep current state
      });

    navigator.permissions
      .query({ name: 'clipboard-read' as PermissionName })
      .then((result) => {
        readPermissionStatus = result;

        if (mounted) {
          setAvailable((prev) => ({
            ...prev,
            read: result.state !== 'denied',
          }));
          result.addEventListener('change', handleReadChange);
        }
      })
      .catch(() => {
        // clipboard-read not queryable (e.g. Firefox), keep current state
      });

    return () => {
      mounted = false;
      writePermissionStatus?.removeEventListener('change', handleWriteChange);
      readPermissionStatus?.removeEventListener('change', handleReadChange);
    };
  }, []);

  return available;
};
